import { useRef, useState, useEffect, useCallback } from 'react'
import { useItemDiscussionLabel } from '@/hooks/useItemDiscussionLabel'

import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Task, TaskLink } from '@/types/task'
import { PanelHeader } from './sections/PanelHeader'
import { PanelNotes } from './sections/PanelNotes'
import { PanelLinks } from './sections/PanelLinks'
import { PanelPhotos } from './sections/PanelPhotos'
import { PanelMightBeRelevant } from './sections/PanelMightBeRelevant'
import { useEntityRelations } from './hooks/useEntityRelations'
import type { MightBeRelevantItem } from './types'
import { PanelShell } from './PanelShell'
import { EventWhenLine } from './sections/EventWhenLine'
import { PanelFacts } from './sections/PanelFacts'
import { PanelActions, foldedClick, type PanelAction } from './sections/PanelActions'
import { PanelSection } from './sections/PanelSection'
import { PanelRow } from './sections/PanelRow'
import { PanelMoreMenu } from './sections/PanelMoreMenu'
import { Checkmark } from './sections/PanelSubtasks'
import { SchedulePicker } from '@/components/schedule/SchedulePicker'
import { dateForWhen } from '@/components/schedule/RescheduleGrid'
import type { TriageWhen } from '@/components/schedule/TriageWhenMenu'

/** The relative days an EVENT can be moved to, keeping the hour it runs at. */
const EVENT_WHENS: readonly TriageWhen[] = ['today', 'tomorrow', 'this-weekend', 'next-weekend', 'next-week']
import { useDayLoads } from './hooks/useDayLoads'
import { PanelLocation } from './sections/PanelLocation'
import { locationLink } from '@/lib/locationLink'
import { ConceptIcon } from '@/lib/conceptIcons'
import { computeEventReschedule } from '@/lib/planning/planningReschedule'
import { AssistDrawer } from '@/components/assist/AssistDrawer'
import { useThreadUnread } from '@/hooks/useThreadUnread'
import { getRecurringBaseId } from '@/hooks/useHiddenCalendarEvents'
import { scopeForCalendarEvent } from '@/lib/scope'

interface TapEventPanelProps {
  event: CalendarEvent
  /** User's notes for the event (from event_notes table). */
  notes: string | undefined
  /** Links saved on this event (from event_notes table). */
  links?: TaskLink[]
  allTasks: Task[]

  /** Whether the event is marked done (actionable_instances, Symphony-side). */
  completed?: boolean
  /**
   * Toggle done/undone. Completion is Symphony state, not a Google write, so
   * it's offered even on read-only calendars. When omitted, the pill hides.
   */
  onToggleComplete?: () => void

  onClose: () => void
  onNotesChange: (next: string) => void
  onAddPrepTask: (title: string) => void
  /** Complete / reopen a prep task in place. When omitted, rows only open. */
  onTogglePrepTask?: (id: string) => void
  onAddLink: (url: string) => void
  onOpenTask: (id: string) => void
  onOpenProject: (id: string) => void
  onOpenRelated: (kind: MightBeRelevantItem['kind'], id: string) => void
  /** Set/change/clear the event's location, syncing back to Google Calendar. */
  onUpdateEventLocation?: (googleEventId: string, location: string | null, calendarId?: string) => void
  /**
   * Rename the event, syncing back to Google Calendar (this instance only for
   * a recurring series). When omitted — or the calendar is read-only — the
   * title stays read-only and an edit attempt reverts.
   */
  onRenameEvent?: (nextTitle: string) => void
  /**
   * Move the event to a new start, keeping its original duration. Pushes back to
   * Google Calendar (this instance only for a recurring series). When omitted,
   * the Reschedule control is hidden.
   */
  /** Returning `false` means Google refused it — the inline editor keeps
   *  the edit and says so rather than closing over a change that did not
   *  happen. A host that returns nothing is taken at its word, as before. */
  onReschedule?: (startTime: Date, endTime: Date) => void | Promise<boolean | void>
  /** The event's calendar, resolved by the caller from the Google calendar list.
   *  readOnly=true means Google will refuse writes — edit affordances hide. */
  calendarAccess?: { name: string | null; readOnly: boolean }
  /** Calendars this account can write to (for "Move to calendar"). */
  writableCalendars?: { id: string; summary: string }[]
  /** Move the event onto another (writable) calendar. */
  onMoveToCalendar?: (destinationCalendarId: string) => void
  /**
   * Delete the event from Google Calendar. Offered only where Google will take
   * the write (the same gate as the inline when-line editor). Resolving
   * `false` means the delete failed: the panel stays open and says so.
   */
  onDeleteEvent?: () => Promise<boolean | void>
  /** Needs-discussion flag — surfaces on the family kiosk's For Discussion list. */
  discussion?: { flagged: boolean; note?: string }
  /** Flag/unflag this event for discussion. When omitted, the Discuss chip hides. */
  onToggleDiscussion?: (flagged: boolean) => void
  /** Save the discussion note ("what's the question?"). */
  onDiscussionNoteChange?: (note: string) => void
  /** Open the Discussion on mount (deep link from the Discussions inbox). */
  autoOpenDiscussion?: boolean
  /**
   * Skip this one occurrence (Symphony-side, never a Google write) — the job
   * the Today row's ⋯ menu did as "Skip today". `label` names the day;
   * pressing again while skipped puts it back. Omitted, no chip.
   */
  skipDay?: { skipped: boolean; label: string; onToggle: () => void }
  /** Informational-only "free" flag: the kids just show up, nothing for a
   *  parent to do. When set, the Complete pill and prep-task input hide. */
  free?: boolean
  /** Toggle the free flag. When omitted, the Free pill hides. */
  onToggleFree?: (free: boolean) => void
  /** True when this event is part of a recurring series — toggling free
   *  writes the series note, so the pill's title says it applies to every occurrence. */
  freeAppliesToSeries?: boolean
}

/** The sections an event can carry, each hidden until it has something in it. */
type EventField = 'location' | 'notes' | 'subtask' | 'photo' | 'link'

/** Same order and wording as the task pane's "+ …" chips, in the event's own words. */
const ADD_CHIPS: { field: EventField; label: string }[] = [
  { field: 'location', label: '+ Place' },
  { field: 'notes', label: '+ What to bring' },
  { field: 'subtask', label: '+ Prep task' },
  { field: 'photo', label: '+ File' },
  { field: 'link', label: '+ Link' },
]

type AnyEvent = { start_time?: string; startTime?: string; end_time?: string; endTime?: string }

function getStartTime(event: CalendarEvent): string | undefined {
  return (event as AnyEvent).start_time || (event as AnyEvent).startTime
}

function getEndTime(event: CalendarEvent): string | undefined {
  return (event as AnyEvent).end_time || (event as AnyEvent).endTime
}

const MINUTES_PER_DAY = 1440

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120]
/**
 * A boarding stay or a trip is measured in days. Offering only 15–120 MINUTES
 * for one meant the menu's every option silently collapsed a five-day booking
 * to at most two hours — the control could destroy the event but never express
 * it.
 */
const MULTI_DAY_PRESETS = [1, 2, 3, 4, 5, 7].map((d) => d * MINUTES_PER_DAY)

function formatDuration(minutes: number): string {
  if (minutes >= MINUTES_PER_DAY) {
    const days = Math.floor(minutes / MINUTES_PER_DAY)
    const hours = Math.round((minutes % MINUTES_PER_DAY) / 60)
    const d = `${days} day${days === 1 ? '' : 's'}`
    return hours === 0 ? d : `${d} ${hours} hr`
  }
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`
}

/** Short day label for the far end of an event that crosses midnight. */
function formatDayShort(iso?: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** Does this event end on a different calendar day than it starts? */
function spansDays(start?: string, end?: string): boolean {
  if (!start || !end) return false
  return new Date(start).toDateString() !== new Date(end).toDateString()
}

function formatDayLabel(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  if (sameDay) return 'Today'
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

function formatClock(iso?: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function TapEventPanel(props: TapEventPanelProps) {
  const { event, allTasks } = props
  const [showDirections, setShowDirections] = useState(false)
  const [showDurationMenu, setShowDurationMenu] = useState(false)
  const [prepDraft, setPrepDraft] = useState('')
  const [assistOpen, setAssistOpen] = useState(props.autoOpenDiscussion === true)
  useEffect(() => { if (props.autoOpenDiscussion) setAssistOpen(true) }, [props.autoOpenDiscussion])

  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Completed prep tasks stay listed, struck through: ticking one off used to
  // make it vanish from the event it was prep for, with no way to reopen it
  // here (event-panel review, finding a).
  const relations = useEntityRelations({
    kind: 'event',
    entity: event,
    allTasks,
    allEvents: [],
    allProjects: [],
    includeCompleted: true,
  })
  // Open work first; done rows sink below it (stable within each group).
  const prepTasks = [...relations.tasks].sort((a, b) => Number(!!a.completed) - Number(!!b.completed))

  const startTime = getStartTime(event)
  const endTime = getEndTime(event)
  const eventId = event.google_event_id ?? event.id
  // One thread per series, not per instance — the same key the wall flag uses.
  const discussionBaseId = getRecurringBaseId(eventId)
  const discussionUnread = useThreadUnread('event', discussionBaseId)
  const discussionLabel = useItemDiscussionLabel('event', discussionBaseId)
  const calendarId = event.calendar_id ?? event.calendarId

  // Google refuses writes to view-only calendars — don't offer them.
  const readOnlyCalendar = props.calendarAccess?.readOnly === true
  const canEdit = !readOnlyCalendar
  const moveTargets = (props.writableCalendars ?? []).filter((c) => c.id !== (calendarId ?? ''))

  const discussionFlagged = props.discussion?.flagged === true

  // Current duration in minutes, when both ends are known.
  const durationMinutes =
    startTime && endTime
      ? Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000)
      : null

  // Keep the start, move the end. This pushes to Google Calendar via the same
  // channel a reschedule uses.
  const handleDurationChange = (minutes: number) => {
    if (!startTime) return
    const start = new Date(startTime)
    props.onReschedule?.(start, new Date(start.getTime() + minutes * 60000))
    setShowDurationMenu(false)
  }

  // Classify the location so a video meeting (Teams/Zoom/Meet) never renders
  // as a physical address with a Directions/Maps affordance.
  const locLink = locationLink(event.location, undefined, event.meeting_url ?? event.meetingUrl)
  const isPhysicalLocation = locLink.kind === 'maps'
  const joinUrl = locLink.kind === 'url' ? locLink.href : null
  const isVirtualMeeting = locLink.kind === 'url' || locLink.kind === 'virtual'

  // SchedulePopover yields the new start as a full Date; reuse the planning
  // reschedule math so the event keeps its original duration.
  /**
   * A relative tile — "Tomorrow", "Next week" — means MOVE THE DAY, keeping
   * the hour the event runs at. The tiles resolve to midnight, so routing them
   * straight through would have dragged a 1pm meeting to midnight.
   *
   * Until now nothing handled them at all: SchedulePicker calls `onReschedule`
   * for a relative tile and this panel passed only `onSchedule`, so every tile
   * but "Pick date & time…" was silent (Scott, 2026-09-24).
   */
  const handleRelativeReschedule = (when: TriageWhen, chosen?: Date) => {
    // A weekend tile names its own day — Saturday or Sunday, whichever the
    // reader picked. Everything else resolves as it always did.
    const day = chosen ?? dateForWhen(when)
    if (!day) return
    const orig = startTime ? new Date(startTime) : null
    handleReschedule(new Date(
      day.getFullYear(), day.getMonth(), day.getDate(),
      orig?.getHours() ?? 9, orig?.getMinutes() ?? 0,
    ))
  }

  const handleReschedule = (date: Date) => {
    const { startTime: newStart, endTime: newEnd } = computeEventReschedule(event, {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    })
    props.onReschedule?.(newStart, newEnd)
  }

  // Deleting is irreversible from here, so PanelMoreMenu's two-step confirm
  // stands in front of it. Close only on success; a refused delete keeps the
  // panel open and says so rather than failing silently (finding c).
  const handleDelete = async () => {
    setDeleteError(null)
    const ok = await props.onDeleteEvent?.()
    if (ok === false) {
      setDeleteError("Couldn't delete this event — it's still on your calendar.")
      return
    }
    props.onClose()
  }

  const commitPrepTask = () => {
    const title = prepDraft.trim()
    if (!title) return
    props.onAddPrepTask(title)
    setPrepDraft('')
  }

  // The whole panel accepts file drops — same reason as the task panel: the
  // Photos & files section alone is a small target and a miss navigates away.
  const panelRef = useRef<HTMLElement>(null)

  // The task pane's rule (detail pane standard): a section earns its header by
  // having something in it. An event used to open on five titled, empty
  // sections — a location box, a whole rich-text editor, three upload tiles —
  // before it said anything. What it doesn't carry is offered as one quiet
  // line of "+ …" chips; a chip opens that section with the cursor in it.
  const [revealed, setRevealed] = useState<Set<EventField>>(() => new Set())
  // Attachments load inside PanelPhotos; it reports back whether there are any.
  const [photosHaveContent, setPhotosHaveContent] = useState(false)
  const [prevEventId, setPrevEventId] = useState(event.id)
  if (event.id !== prevEventId) {
    setPrevEventId(event.id)
    setRevealed(new Set())
    setPhotosHaveContent(false)
  }
  const has: Record<EventField, boolean> = {
    location: !!event.location,
    notes: !!(props.notes ?? '').replace(/<[^>]*>/g, '').trim(),
    subtask: prepTasks.length > 0,
    photo: photosHaveContent,
    link: (props.links?.length ?? 0) > 0,
  }
  const show = (field: EventField): boolean => has[field] || revealed.has(field)
  const addChips = ADD_CHIPS.filter((c) => {
    if (show(c.field)) return false
    // A virtual meeting's place is its Join link, not an address.
    if (c.field === 'location') return !isVirtualMeeting && !!props.onUpdateEventLocation
    // A free event has nothing for a parent to prepare.
    if (c.field === 'subtask') return !props.free
    return true
  })
  const reveal = useCallback((field: EventField) => {
    setRevealed((prev) => new Set(prev).add(field))
    // What to bring's editor is a lazy chunk, so its contenteditable can
    // arrive a beat after the section does: keep looking for up to a second.
    let tries = 0
    const focusWhenReady = () => {
      const host = panelRef.current?.querySelector<HTMLElement>(`[data-prep-field="${field}"]`)
      const target = host?.querySelector<HTMLElement>('textarea, input:not([type="hidden"]):not([type="file"]), [contenteditable="true"]')
      if (host && tries === 0) host.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
      if (target) { target.focus({ preventScroll: true }); return }
      if (++tries < 20 && field !== 'photo') setTimeout(focusWhenReady, 50)
    }
    requestAnimationFrame(focusWhenReady)
  }, [])

  const dayLoads = useDayLoads({ tasks: props.allTasks ?? [], enabled: true })

  // The duration control owns its own popover, so it arrives as a rendered node
  // rather than a plain chip. Extracted verbatim from the old hand-built header.
  const durationMenu = durationMinutes === null ? null : (
            <div className="relative">
              <button
                onClick={() => setShowDurationMenu((v) => !v)}
                aria-expanded={showDurationMenu}
                aria-label="Change duration"
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[13px] text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
              >
                {formatDuration(durationMinutes)} {showDurationMenu ? '▾' : '▸'}
              </button>
              {showDurationMenu && (
                <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-neutral-100 py-1 min-w-[7rem]">
                  {(durationMinutes >= MINUTES_PER_DAY ? MULTI_DAY_PRESETS : DURATION_PRESETS).map((m) => (
                    <button
                      key={m}
                      onClick={() => handleDurationChange(m)}
                      className={`block w-full text-left px-4 py-2 text-[15px] transition-colors hover:bg-neutral-50 ${
                        m === durationMinutes ? 'text-primary-700 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      {formatDuration(m)}
                    </button>
                  ))}
                </div>
              )}
            </div>
  )

  // Reschedule and the duration are the same fact as the when-line, so they sit
  // in the When row beside it rather than in the action row (detail pane
  // standard, 2026-10-19).
  const reschedulePicker = canEdit && props.onReschedule ? (
    <SchedulePicker
      trigger="quiet"
      label="Reschedule"
      scheduledFor={startTime ? new Date(startTime) : undefined}
      onSchedule={(date) => handleReschedule(date)}
      onReschedule={handleRelativeReschedule}
      // The days an event can be MOVED to, keeping its time. "Tonight"
      // and the pool tiles are left out on purpose: an event cannot go
      // to Someday, and "Tonight" would mean inventing an hour. A new
      // time is a job for "Pick date & time…", which was already the
      // only tile that worked.
      whens={EVENT_WHENS}
      // An event ends at a particular time, so "either day" is not an
      // answer it can take: the weekend offers Saturday AND Sunday.
      weekendDays
      loads={dayLoads}
    />
  ) : null
  const showDuration = canEdit && !!props.onReschedule && durationMinutes !== null && durationMinutes > 0

  const actions: PanelAction[] = [
    ...(props.onToggleComplete && !props.free
      ? [{
          id: 'complete',
          label: props.completed ? 'Completed' : 'Complete',
          kind: (props.completed ? 'completed' : 'primary') as PanelAction['kind'],
          onClick: props.onToggleComplete,
        }]
      : []),
    ...(event.location && joinUrl
      ? [{ id: 'join', label: 'Join meeting', icon: 'video' as const, href: joinUrl }]
      : []),
    ...(event.location && isPhysicalLocation
      ? [{
          id: 'directions',
          label: 'Directions',
          icon: 'location' as const,
          onClick: () => setShowDirections((v) => !v),
        }]
      : []),
    {
      id: 'discussion',
      label: discussionLabel,
      icon: 'discussion' as const,
      dot: discussionUnread,
      onClick: () => setAssistOpen(true),
    },
    ...(props.onToggleDiscussion
      ? [{
          id: 'discuss',
          label: discussionFlagged ? 'On the list' : 'Bring up',
          kind: (discussionFlagged ? 'flagged' : 'default') as PanelAction['kind'],
          pressed: discussionFlagged,
          title: discussionFlagged
            ? "Take it off the wall's For Discussion list"
            : "Bring up in person — adds it to the wall's For Discussion list",
          onClick: () => props.onToggleDiscussion?.(!discussionFlagged),
        }]
      : []),
    ...(props.onToggleFree
      ? [{
          id: 'free',
          label: 'Free',
          kind: (props.free ? 'flagged' : 'default') as PanelAction['kind'],
          pressed: !!props.free,
          title: 'The kids just show up — no prep, no handoff, nothing for a parent to do.'
            + (props.freeAppliesToSeries ? ' Applies to every occurrence.' : ''),
          onClick: () => props.onToggleFree?.(!props.free),
        }]
      : []),
    ...(props.skipDay && !props.completed
      ? [{
          id: 'skip-day',
          label: props.skipDay.skipped ? 'Put it back' : props.skipDay.label,
          kind: (props.skipDay.skipped ? 'flagged' : 'default') as PanelAction['kind'],
          pressed: props.skipDay.skipped,
          title: props.skipDay.skipped
            ? 'Skipped for this day only — put it back'
            : 'Skip this occurrence only — the calendar is unchanged',
          onClick: props.skipDay.onToggle,
        }]
      : []),
  ]

  return (
    <PanelShell
      innerRef={panelRef}
      identity={
        // One wrapper, not a fragment: title, when-line and calendar row are a
        // single statement of what this event IS. As separate zone children the
        // shell's rhythm would rule between them and read as three facts.
        <div>
        <PanelHeader
          kind="Event"
          title={event.title}
          // Renames write through to Google, so they're only offered where
          // Google will accept the write; on a view-only calendar (or a host
          // that didn't wire renames) the edit reverts, matching the pill.
          onTitleChange={
            props.onRenameEvent && !readOnlyCalendar
              ? props.onRenameEvent
              : () => { /* event title is read-only */ }
          }
          onClose={props.onClose}
        />

        <PanelFacts
          facts={[
            {
              // When — the one fact that defines an event, stated plainly, and
              // editable in place where Google will take the write (Scott's
              // walkthrough: "click the date/time under the title to edit it").
              id: 'when',
              label: 'When',
              value: startTime ? (
                <>
                  <EventWhenLine
                    event={event}
                    startTime={new Date(startTime)}
                    endTime={endTime ? new Date(endTime) : null}
                    spansDays={!!endTime && spansDays(startTime, endTime)}
                    onSave={canEdit && props.onReschedule ? props.onReschedule : undefined}
                  >
                    <span className="flex flex-wrap items-baseline gap-x-1.5">
                      <span className="font-medium text-neutral-800">{formatDayLabel(startTime)}</span>
                      {/* A multi-day event must name the day it ends on. Without it a
                          five-day boarding stay read "8:00 AM – 8:00 AM", which looks
                          like a bug rather than a fact about the event. */}
                      <span className="text-neutral-600 tabular-nums">
                        {formatClock(startTime)}
                        {endTime
                          ? spansDays(startTime, endTime)
                            ? ` – ${formatDayShort(endTime)}, ${formatClock(endTime)}`
                            : ` – ${formatClock(endTime)}`
                          : ''}
                      </span>
                      {!showDuration && durationMinutes !== null && durationMinutes > 0 && (
                        <span className="text-[13px] text-neutral-500">· {formatDuration(durationMinutes)}</span>
                      )}
                    </span>
                  </EventWhenLine>
                  {showDuration && durationMenu}
                  {reschedulePicker}
                </>
              ) : null,
            },
            {
              // Which calendar this event lives on + move / view-only affordance
              id: 'calendar',
              label: 'Calendar',
              value: props.calendarAccess ? (
                <>
                  <span className="text-neutral-800">
                    {props.calendarAccess.name ?? 'Primary calendar'}
                  </span>
                  {readOnlyCalendar ? (
                    <span
                      className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500 text-[11px] font-medium"
                      title="This calendar is shared with you view-only — Google doesn't allow Symphony (or you) to change its events"
                    >
                      view-only
                    </span>
                  ) : (
                    props.onMoveToCalendar && moveTargets.length > 0 && (
                      <select
                        aria-label="Move to calendar"
                        value=""
                        onChange={(e) => { if (e.target.value) props.onMoveToCalendar?.(e.target.value) }}
                        className="appearance-none w-auto max-w-[7rem] self-start text-[13px] text-neutral-400 !bg-transparent border-0 p-0 cursor-pointer hover:text-neutral-700 hover:underline focus:outline-none focus-visible:underline"
                      >
                        <option value="">Move to…</option>
                        {moveTargets.map((c) => (
                          <option key={c.id} value={c.id}>{c.summary}</option>
                        ))}
                      </select>
                    )
                  )}
                </>
              ) : null,
            },
            {
              id: 'where',
              label: 'Where',
              value: event.location && isVirtualMeeting && !joinUrl
                ? <span className="text-neutral-700">{event.location}</span>
                : null,
            },
          ]}
        />
        </div>
      }
      act={
        <>
          <PanelActions
            actions={actions}
            // Same gate as the when-line editor: a view-only calendar (or a
            // host that didn't wire it) gets no Delete at all. With Delete,
            // the folded actions ride in the same ⋯ rather than a second one.
            renderOverflow={canEdit && props.onDeleteEvent
              ? (folded) => <PanelMoreMenu items={folded.map((a) => ({ id: a.id, label: a.label, icon: a.icon, onClick: foldedClick(a) }))} onDelete={() => { void handleDelete() }} />
              : undefined}
          />
          {deleteError && (
            <p role="alert" className="mt-2 text-[13px] text-rose-700">{deleteError}</p>
          )}
        </>
      }
      details={
        <>
          {/* Location editor: for physical addresses or to add one. A virtual
              meeting is handled by the Join chip above, not a Places field. */}
          {!isVirtualMeeting && show('location') && (<div data-prep-field="location">
            <PanelLocation
              location={event.location ?? undefined}
              title={event.title}
              showDirections={isPhysicalLocation && showDirections}
              onUpdateLocation={(addr) => props.onUpdateEventLocation?.(eventId, addr, calendarId)}
              onClearLocation={() => props.onUpdateEventLocation?.(eventId, null, calendarId)}
            />
          </div>)}

          {show('notes') && (<div data-prep-field="notes">
            <PanelNotes
              key={event.id}
              notes={props.notes}
              onChange={props.onNotesChange}
              label="What to bring"
              id="what-to-bring"
            />
          </div>)}

          {discussionFlagged && props.onDiscussionNoteChange && (
            <PanelSection
              id="for-discussion"
              label="For discussion"
              preview={props.discussion?.note || undefined}
            >
              <textarea
                key={eventId}
                defaultValue={props.discussion?.note ?? ''}
                onBlur={(e) => {
                  if ((e.target.value || '') !== (props.discussion?.note ?? '')) {
                    props.onDiscussionNoteChange?.(e.target.value)
                  }
                }}
                aria-label="What's the question?"
                placeholder="What's the question?"
                rows={2}
                className="w-full px-2 py-1.5 text-[15px] rounded-md bg-white text-neutral-700 placeholder:text-neutral-400 shadow-[inset_0_0_0_1px_#e5e7eb] focus:outline-none focus:shadow-[inset_0_0_0_1px_#d97706] resize-none"
              />
            </PanelSection>
          )}

          {/* Prep tasks are the event's subtasks — real tasks linked to this event. */}
          {show('subtask') && (<div data-prep-field="subtask">
            <PanelSection
              id="prep-tasks"
              label="Prep tasks"
              preview={prepTasks.length ? `${prepTasks.length} task${prepTasks.length === 1 ? '' : 's'}` : undefined}
            >
              <div className="flex flex-col gap-1.5">
                {prepTasks.map((t) => {
                  const row = (
                    <PanelRow
                      key={t.id}
                      onClick={() => props.onOpenTask(t.id)}
                      icon={<span className="w-6 h-6 flex items-center justify-center rounded-md bg-amber-100"><ConceptIcon name="list" decorative /></span>}
                    >
                      <span className={`block text-[15px] ${t.completed ? 'line-through text-neutral-400' : 'text-neutral-800'}`}>{t.title}</span>
                    </PanelRow>
                  )
                  if (!props.onTogglePrepTask) return row
                  // Split row, as subtasks do: the mark completes or reopens,
                  // the title opens the task.
                  return (
                    <div key={t.id} className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => props.onTogglePrepTask?.(t.id)}
                        aria-label={`Mark ${t.title} ${t.completed ? 'incomplete' : 'complete'}`}
                        className="mt-2.5 flex-shrink-0"
                      >
                        <Checkmark completed={!!t.completed} />
                      </button>
                      <div className="min-w-0 flex-1">{row}</div>
                    </div>
                  )
                })}
                {!props.free && (
                  <input
                    type="text"
                    value={prepDraft}
                    onChange={(e) => setPrepDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitPrepTask() }}
                    onBlur={commitPrepTask}
                    aria-label="Add a prep task"
                    placeholder="+ Add a prep task…"
                    className="text-[15px] px-2 py-1.5 rounded-md bg-transparent text-neutral-500 placeholder:text-neutral-400 focus:outline-none focus:bg-neutral-50 hover:bg-neutral-50"
                  />
                )}
              </div>
            </PanelSection>
          </div>)}

          {/* Attachments key on the stable Google event id (the same key event
              notes use), under the 'event_note' entity type the table allows. */}
          <div data-prep-field="photo" className="empty:hidden"><PanelPhotos
            hideWhenEmpty={!revealed.has('photo')}
            onContentChange={setPhotosHaveContent}
            entityType="event_note"
            entityId={eventId}
            dropZoneRef={panelRef}
            entityContext={[event.title, startTime && new Date(startTime).toLocaleString(), event.location].filter(Boolean).join(' — ')}
            promotions={{
              onAddPrepTask: props.onAddPrepTask,
              onAddLink: props.onAddLink,
              onUseLocation: props.onUpdateEventLocation && canEdit
                ? (address) => props.onUpdateEventLocation!(eventId, address, calendarId)
                : undefined,
            }}
          /></div>

          {show('link') && <div data-prep-field="link"><PanelLinks links={props.links} onAddLink={props.onAddLink} /></div>}

          {addChips.length > 0 && (
            <div className="prep-add" role="group" aria-label="Add to this event">
              {addChips.map((c) => (
                <button key={c.field} type="button" className="canvas-chip" onClick={() => reveal(c.field)}>{c.label}</button>
              ))}
            </div>
          )}
        </>
      }
      related={<PanelMightBeRelevant items={[]} onOpen={props.onOpenRelated} />}
      // No footer: events carry no created/updated timestamps, and the one
      // this used to show was the START time labelled "Created".
    >
      {assistOpen && (
        <AssistDrawer
          item={{ id: eventId, title: event.title, notes: props.notes ?? null }}
          discuss={{ type: 'event', id: discussionBaseId, title: event.title, scope: scopeForCalendarEvent() }}
          onClose={() => setAssistOpen(false)}
        />
      )}
    </PanelShell>
  )
}
